// ###### IMPORTS
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { ArrowLeftIcon, XMarkIcon } from '@heroicons/react/24/solid';
import api from 'connections/api';
import routes_api from 'connections/routes_api';
import routes_web from 'connections/routes_web';
import SpinnerLoad from './moduleComponents/SpinnerLoad';
import useViewportHeight from 'services/useViewportHeight';

// ###### MAIN COMPONENT
const Register = () => {
  const navigate = useNavigate();
  
  // ##### HOOKS
  useViewportHeight(); // Set real viewport height for mobile
  
  const [currentStep, setCurrentStep] = useState(1);
  const [inviteCode, setInviteCode] = useState('');
  const [codeValidation, setCodeValidation] = useState(null);
  const [userData, setUserData] = useState({
    username: '',
    password: '',
    confirmPassword: '',
    first_name: '',
    email: '',
    date_birth: '',
    br_cpf: '',
    phone: '',
    app_initial: 'accounts'
  });
  const [isValidatingCode, setIsValidatingCode] = useState(false);
  const [isCreatingAccount, setIsCreatingAccount] = useState(false);
  const [isCheckingUsername, setIsCheckingUsername] = useState(false);
  const [showAdditionalFields, setShowAdditionalFields] = useState(false);
  const [errors, setErrors] = useState({});
  const [usernameStatus, setUsernameStatus] = useState(null); // 'checking', 'available', 'unavailable', 'error'
  const [passwordStatus, setPasswordStatus] = useState(null); // 'valid', 'invalid', 'mismatch'
  const [isPWA, setIsPWA] = useState(false);

  useEffect(() => {
    setIsPWA(window.matchMedia('(display-mode: standalone)').matches || 
             window.navigator.standalone === true);
  }, []);

  // ##### HANDLERS
  const handleCodeCheck = async () => {
    if (!inviteCode || inviteCode.length !== 6) {
      setErrors({ code: 'Code must be 6 characters' });
      return;
    }

    setIsValidatingCode(true);
    setErrors({});

    try {
      const response = await api.post(routes_api.invite_validate, {
        invite_code: inviteCode.toUpperCase()
      });

      if (response.data.valid) {
        setCodeValidation(response.data);
        toast.success('Code validated successfully!');
      } else {
        setErrors({ code: 'Invalid or expired code' });
        toast.error('Invalid invite code');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.errors?.invite_code?.[0] || 
                          error.response?.data?.error || 
                          'Failed to validate code';
      setErrors({ code: errorMessage });
      toast.error(errorMessage);
    } finally {
      setIsValidatingCode(false);
    }
  };

  const handleNextStep = async () => {
    // Reset status
    setUsernameStatus(null);
    setPasswordStatus(null);
    setErrors({});

    // Validate password
    if (!userData.password || userData.password.length < 8) {
      setPasswordStatus('invalid');
      return;
    }
    if (userData.password !== userData.confirmPassword) {
      setPasswordStatus('mismatch');
      return;
    }
    if (userData.password.length >= 8) {
      setPasswordStatus('valid');
    }

    // Validate username
    if (!userData.username || userData.username.length < 3) {
      setErrors({ username: 'Username must be at least 3 characters' });
      return;
    }

    setIsCheckingUsername(true);
    setUsernameStatus('checking');

    try {
      const response = await api.post(routes_api.invite_check_username, {
        username: userData.username
      });

      if (response.data.available) {
        setUsernameStatus('available');
        console.log('=== SHOWING ADDITIONAL FIELDS ===');
        console.log('Window height:', window.innerHeight);
        console.log('CSS --vh:', getComputedStyle(document.documentElement).getPropertyValue('--vh'));
        console.log('Container height will be:', getComputedStyle(document.documentElement).getPropertyValue('--vh') ? 
          `${parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--vh')) * 100}px` : '--vh not set');
        setShowAdditionalFields(true);
      } else {
        setUsernameStatus('unavailable');
        setErrors({ username: response.data.message });
      }
    } catch (error) {
      setUsernameStatus('error');
      const errorMessage = error.response?.data?.error || 'Failed to check username availability';
      setErrors({ username: errorMessage });
    } finally {
      setIsCheckingUsername(false);
    }
  };

  const handleRegister = async () => {
    // Validate all fields
    const newErrors = {};
    if (!userData.first_name) {
      newErrors.first_name = 'First name is required';
    }
    if (!userData.email) {
      newErrors.email = 'Email is required';
    }
    if (!userData.date_birth) {
      newErrors.date_birth = 'Date of birth is required';
    }
    if (!userData.br_cpf) {
      newErrors.br_cpf = 'CPF is required';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsCreatingAccount(true);
    setErrors({});

    try {
      const response = await api.post(routes_api.invite_register, {
        invite_code: inviteCode.toUpperCase(),
        username: userData.username,
        password: userData.password,
        first_name: userData.first_name,
        email: userData.email,
        date_birth: userData.date_birth,
        br_cpf: userData.br_cpf,
        phone: userData.phone,
        app_initial: userData.app_initial
      });

      if (response.data.success) {
        setCurrentStep(3);
        toast.success('Account created successfully!');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.errors?.username?.[0] ||
                          error.response?.data?.errors?.password?.[0] ||
                          error.response?.data?.errors?.invite_code?.[0] ||
                          error.response?.data?.error || 
                          'Registration failed';
      setErrors({ general: errorMessage });
      toast.error(errorMessage);
    } finally {
      setIsCreatingAccount(false);
    }
  };

  const handleLoginRedirect = () => {
    navigate(routes_web.accounts_login);
  };

  const handleResetCode = () => {
    setInviteCode('');
    setCodeValidation(null);
    setShowAdditionalFields(false);
    setUsernameStatus(null);
    setPasswordStatus(null);
    setErrors({});
    setUserData({
      username: '',
      password: '',
      confirmPassword: '',
      first_name: '',
      email: '',
      date_birth: '',
      br_cpf: '',
      phone: '',
      app_initial: 'accounts'
    });
  };

  // ##### RENDER FUNCTIONS
  const renderCreateForm = () => (
    <div className="max-w-md mx-auto">
      <div className="bg-gray-800 rounded-lg p-8 shadow-xl">
        <div className="relative flex items-center justify-center mb-8 pt-4">
          {!isPWA && (
            <button 
              type="button" 
              onClick={() => navigate(routes_web.pages_home)}
              className="absolute -left-4 -top-4 p-2 rounded-full bg-gray-700 hover:bg-gray-600 transition"
            >
              <ArrowLeftIcon className="h-6 w-6 text-gray-300" />
            </button>
          )}
          <div className="text-center">
            <h1 className="text-3xl font-bold text-white mb-2">Create Account</h1>
            <p className="text-gray-400">
              {codeValidation ? 'Complete your account details' : 'Enter your invite code to get started'}
            </p>
          </div>
        </div>

        <div className="space-y-6">
          {/* Invite Code Section */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Invite Code
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={inviteCode}
                onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleCodeCheck();
                }}
                placeholder="Enter 6-character code"
                maxLength={6}
                disabled={!!codeValidation}
                className={`flex-1 min-w-0 px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  codeValidation ? 'bg-green-900 border-green-600' : ''
                }`}
              />
              {codeValidation ? (
                <button
                  onClick={handleResetCode}
                  className="px-2 py-3 bg-gray-600 hover:bg-gray-500 text-gray-300 hover:text-white font-medium rounded-lg transition duration-200 flex-shrink-0"
                  title="Reset invite code"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              ) : (
                <button
                  onClick={handleCodeCheck}
                  disabled={isValidatingCode || !inviteCode}
                  className="px-2 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-medium rounded-lg transition duration-200 flex-shrink-0 w-16 flex items-center justify-center"
                >
                  {isValidatingCode ? <SpinnerLoad size="small" /> : 'Check'}
                </button>
              )}
            </div>
            {errors.code && (
              <p className="mt-2 text-sm text-red-400">{errors.code}</p>
            )}
            {codeValidation && (
              <p className="mt-2 text-sm text-green-400">✓ Code validated successfully!</p>
            )}
          </div>

          {/* Permissions Preview */}
          {codeValidation && (
            <div className="p-4 bg-gray-700 rounded-lg">
              <h3 className="text-sm font-medium text-gray-300 mb-2">Available modules for this invite:</h3>
              <div className="flex flex-wrap gap-2">
                <span className="px-2 py-1 bg-green-600 text-white text-xs rounded">Basic access</span>
                {codeValidation.permissions.core && (
                  <span className="px-2 py-1 bg-blue-600 text-white text-xs rounded">Core</span>
                )}
                {codeValidation.permissions.health && (
                  <span className="px-2 py-1 bg-blue-600 text-white text-xs rounded">Health</span>
                )}
                {codeValidation.permissions.finance && (
                  <span className="px-2 py-1 bg-blue-600 text-white text-xs rounded">Finance</span>
                )}
                {codeValidation.permissions.travel && (
                  <span className="px-2 py-1 bg-blue-600 text-white text-xs rounded">Travel</span>
                )}
                {codeValidation.permissions.planner && (
                  <span className="px-2 py-1 bg-blue-600 text-white text-xs rounded">Planner</span>
                )}
              </div>
            </div>
          )}

          {/* Account Details Section */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Username
            </label>
            <input
              type="text"
              value={userData.username}
              onChange={(e) => setUserData({...userData, username: e.target.value})}
              placeholder="Choose a username"
              disabled={!codeValidation}
              className={`w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                !codeValidation ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            />
            {errors.username && (
              <p className="mt-2 text-sm text-red-400">{errors.username}</p>
            )}
            {usernameStatus === 'available' && (
              <p className="mt-2 text-sm text-green-400">✓ Username available</p>
            )}
            {usernameStatus === 'checking' && (
              <p className="mt-2 text-sm text-blue-400">Checking availability...</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Password
            </label>
            <input
              type="password"
              value={userData.password}
              onChange={(e) => setUserData({...userData, password: e.target.value})}
              placeholder="Create a password"
              disabled={!codeValidation}
              className={`w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                !codeValidation ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            />
            {errors.password && (
              <p className="mt-2 text-sm text-red-400">{errors.password}</p>
            )}
            <p className="mt-2 text-sm text-gray-400">
              • At least 8 characters
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Confirm Password
            </label>
            <input
              type="password"
              value={userData.confirmPassword}
              onChange={(e) => setUserData({...userData, confirmPassword: e.target.value})}
              placeholder="Confirm your password"
              disabled={!codeValidation}
              className={`w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                !codeValidation ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            />
            {errors.confirmPassword && (
              <p className="mt-2 text-sm text-red-400">{errors.confirmPassword}</p>
            )}
            {passwordStatus === 'invalid' && (
              <p className="mt-2 text-sm text-red-400">Password must be at least 8 characters</p>
            )}
            {passwordStatus === 'mismatch' && (
              <p className="mt-2 text-sm text-red-400">Passwords do not match</p>
            )}
            {passwordStatus === 'valid' && (
              <p className="mt-2 text-sm text-green-400">✓ Password requirements met</p>
            )}
          </div>

          {errors.general && (
            <div className="p-3 bg-red-900 border border-red-700 rounded-lg">
              <p className="text-sm text-red-300">{errors.general}</p>
            </div>
          )}

          {!showAdditionalFields ? (
            <button
              onClick={handleNextStep}
              disabled={isCheckingUsername || !codeValidation}
              className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-medium py-3 px-4 rounded-lg transition duration-200 flex items-center justify-center"
            >
              {isCheckingUsername ? (
                <>
                  <SpinnerLoad size="small" />
                  <span className="ml-2">Checking...</span>
                </>
              ) : (
                'Next'
              )}
            </button>
          ) : null}

          {/* Additional Fields with Accordion Effect */}
          <div className={`transition-all duration-500 ease-in-out overflow-hidden ${
            showAdditionalFields 
              ? 'max-h-none opacity-100 transform translate-y-0' 
              : 'max-h-0 opacity-0 transform -translate-y-2'
          }`}>
            {showAdditionalFields && console.log('=== ACCORDION EXPANDING ===', 'max-h-screen applied')}
            {showAdditionalFields && (
              <>
                {/* Additional Fields */}
                <div className="space-y-6 border-t border-gray-700 pt-6">
                <h3 className="text-lg font-medium text-white mb-4">Complete Your Profile</h3>
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    First Name *
                  </label>
                  <input
                    type="text"
                    value={userData.first_name}
                    onChange={(e) => setUserData({...userData, first_name: e.target.value})}
                    placeholder="Enter your first name"
                    className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  {errors.first_name && (
                    <p className="mt-2 text-sm text-red-400">{errors.first_name}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    value={userData.email}
                    onChange={(e) => setUserData({...userData, email: e.target.value})}
                    placeholder="Enter your email"
                    className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  {errors.email && (
                    <p className="mt-2 text-sm text-red-400">{errors.email}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Date of Birth *
                  </label>
                  <input
                    type="date"
                    value={userData.date_birth}
                    onChange={(e) => setUserData({...userData, date_birth: e.target.value})}
                    className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  {errors.date_birth && (
                    <p className="mt-2 text-sm text-red-400">{errors.date_birth}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Brazilian CPF *
                  </label>
                  <input
                    type="text"
                    value={userData.br_cpf}
                    onChange={(e) => setUserData({...userData, br_cpf: e.target.value})}
                    placeholder="000.000.000-00"
                    maxLength={14}
                    className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  {errors.br_cpf && (
                    <p className="mt-2 text-sm text-red-400">{errors.br_cpf}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Phone
                  </label>
                  <input
                    type="tel"
                    value={userData.phone}
                    onChange={(e) => setUserData({...userData, phone: e.target.value})}
                    placeholder="(00) 00000-0000"
                    className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Initial App
                  </label>
                  <select
                    value={userData.app_initial}
                    onChange={(e) => setUserData({...userData, app_initial: e.target.value})}
                    className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="accounts">Ecosystem (Default)</option>
                    {codeValidation?.permissions.core && <option value="core">Core</option>}
                    {codeValidation?.permissions.health && <option value="health">Health</option>}
                    {codeValidation?.permissions.finance && <option value="finance">Finance</option>}
                    {codeValidation?.permissions.travel && <option value="travel">Travel</option>}
                    {codeValidation?.permissions.planner && <option value="planner">Planner</option>}
                  </select>
                </div>
              </div>

                <button
                  onClick={handleRegister}
                  disabled={isCreatingAccount}
                  className="w-full bg-green-600 hover:bg-green-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-medium py-3 px-4 rounded-lg transition duration-200 flex items-center justify-center mt-4"
                >
                  {isCreatingAccount ? (
                    <>
                      <SpinnerLoad size="small" />
                      <span className="ml-2">Creating Account...</span>
                    </>
                  ) : (
                    'Create Account'
                  )}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  const renderCreateSuccess = () => (
    <div className="max-w-md mx-auto">
      <div className="bg-gray-800 rounded-lg p-8 shadow-xl text-center">
        <div className="relative flex items-center justify-center mb-6">
          {!isPWA && (
            <button 
              type="button" 
              onClick={() => navigate(routes_web.home)}
              className="absolute -left-4 -top-4 p-2 rounded-full bg-gray-700 hover:bg-gray-600 transition"
            >
              <ArrowLeftIcon className="h-6 w-6 text-gray-300" />
            </button>
          )}
          <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center mx-auto">
            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
        </div>
        
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-white mb-2">Success!</h1>
          <p className="text-gray-400">Your account has been created successfully</p>
        </div>

        <button
          onClick={handleLoginRedirect}
          className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-4 rounded-lg transition duration-200"
        >
          Go to Login
        </button>
      </div>
    </div>
  );


  // ##### RETURN
  return (
    <div 
      className="bg-gray-900 flex items-center justify-center p-4"
      style={{ minHeight: 'calc(var(--vh, 1vh) * 100)' }}
    >
      <div className="w-full max-w-md">
        {currentStep === 3 ? renderCreateSuccess() : renderCreateForm()}
      </div>
    </div>
  );
};

export default Register;
